import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { textStyles } from '../../styles/textstyle';
import { CartCalculation } from '../../types/shop';
import DiscountDetails from './DiscountDetails';
import { PointsRedemptionResponse } from '../../api/profile';

interface OrderSummaryProps {
  cartCalculation: CartCalculation;
  shippingMethodSelected?: boolean;
  appliedPoints?: PointsRedemptionResponse | null;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ 
  cartCalculation, 
  shippingMethodSelected = false,
  appliedPoints,
}) => {
  const { t } = useTranslation();
  
  // Handle both old and new API response structures
  const display = cartCalculation.display;
  const currency = cartCalculation.currency || 'HK$';

  // Extract values directly from cartCalculation.breakdown or fallback to display
  const totalItemsValue = (cartCalculation.breakdown)?.totalItemsValue ?? (display?.totalItemsValue ? parseFloat(display.totalItemsValue.replace(/[^0-9.-]+/g, '')) : 0);
  const subtotal = (cartCalculation.breakdown)?.subtotal ?? (display?.subtotal ? parseFloat(display.subtotal.replace(/[^0-9.-]+/g, '')) : 0);
  const totalDiscount = (cartCalculation.breakdown)?.totalDiscount ?? (display?.totalDiscount ? parseFloat(display.totalDiscount.replace(/[^0-9.-]+/g, '')) : 0);
  const shippingCost = (cartCalculation.breakdown)?.shippingCost ?? (display?.shippingCost ? parseFloat(display.shippingCost.replace(/[^0-9.-]+/g, '')) : 0);
  const finalTotal = (cartCalculation.breakdown)?.finalTotal ?? (display?.finalTotal ? parseFloat(display.finalTotal.replace(/[^0-9.-]+/g, '')) : 0);



  // Get applied discounts from new structure or fallback to old structure
  const appliedDiscounts = cartCalculation.appliedDiscounts || cartCalculation.validatedDiscounts?.appliedDiscounts || [];

  // Format currency values
  const formatCurrency = (value: number) => `${currency}${value.toFixed(2)}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        backgroundColor: '#F2EFEA',
        border: '1px solid #E1DACA',
        borderRadius: '4px',
      }}
    >
      <Typography variant="h6" sx={textStyles.h6} mb={2}>
        {t('checkout.orderSummary')}
      </Typography>
      
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography sx={textStyles.orderSummaryLabel}>{t('checkout.subtotal')}</Typography>
        <Typography sx={textStyles.orderSummaryLabel}>
          {formatCurrency(subtotal)}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography sx={textStyles.orderSummaryValue}>
          {t('checkout.totalItemsValue')}
        </Typography>
        <Typography sx={textStyles.orderSummaryValue}>
          {formatCurrency(totalItemsValue)}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography sx={textStyles.orderSummaryValue}>
          {t('checkout.totalDiscount')}
        </Typography>
        <Typography sx={textStyles.orderSummaryValue}>
          {formatCurrency(totalDiscount)}
        </Typography>
      </Box>
      
      {/* Discount Details */}
      {(appliedDiscounts.length > 0 || (display?.productDiscount && display?.productDiscount !== '-HK$0.00') || appliedPoints) && (
        <DiscountDetails
          appliedDiscounts={appliedDiscounts}
          currency={currency}
          display={display || { productDiscount: '', promoDiscount: null, totalDiscount: '' }}
          cartItems={cartCalculation.items || []}
          appliedPoints={appliedPoints}
        />
      )}
      {/* Shipping - only show when shipping method is selected */}
      {shippingMethodSelected && (
        <Box display="flex" justifyContent="space-between" mb={1} mt={2}>
          <Typography sx={textStyles.orderSummaryUnderlined}>
            {t('checkout.shipping')}
          </Typography>
          <Typography sx={textStyles.orderSummaryValue}>
            {formatCurrency(shippingCost)}
          </Typography>
        </Box>
      )}
      <Divider sx={{ my: 2, color: '#ECECEC' }} />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography sx={textStyles.orderSummaryTotal}>
          {t('checkout.orderTotal')}
        </Typography>
        <Typography sx={textStyles.orderSummaryTotal}>
          {formatCurrency(finalTotal)}
        </Typography>
      </Box>
    </Paper>
  );
};

export default OrderSummary; 